import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import '../LoopinoDesktopGuidelines.css';
import '../DesktopPostReviewPage/DesktopPostReviewPage.css';
import '../DesktopCompanyDNA/DesktopCompanyDNAPage.css';
import './DesktopNewStrategyPage.css';
import useCampaign from '../../../hooks/useCampaign';
import useCompany from '../../../hooks/useCompany';
import useLanguage from '../../../hooks/useLanguage';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { Skeleton } from '../../effects';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import { hasCompanyDna } from '../../../utils/companyDna';
import iconTarget from '../../../assets/images/icons/icon-target.svg';
import iconDescription from '../../../assets/images/icons/icon-description.svg';
import iconFrequency from '../../../assets/images/icons/icon-frequency.svg';
import iconCalendar from '../../../assets/images/icons/icon-calendar.svg';
import iconSocial from '../../../assets/images/icons/icon-social.svg';
import iconTone from '../../../assets/images/icons/icon-tone.svg';
import iconInstagram from '../../../assets/images/icons/icon-instagram.svg';
import iconPdf from '../../../assets/images/icons/icon-pdf.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';

const TONE_OPTIONS = [
  { value: 'Professional', translationKey: 'professional' },
  { value: 'Friendly', translationKey: 'friendly' },
  { value: 'Casual', translationKey: 'casual' },
  { value: 'Formal', translationKey: 'formal' },
  { value: 'Enthusiastic', translationKey: 'enthusiastic' }
];

const OBJECTIVE_OPTIONS = [
  { value: 'Awareness', apiValue: 'brand_awareness', translationKey: 'awareness' },
  { value: 'Conversion', apiValue: 'lead_generation', translationKey: 'conversion' },
  { value: 'Engagement', apiValue: 'engagement', translationKey: 'engagement' },
  { value: 'Lead Generation', apiValue: 'lead_generation', translationKey: 'leadGeneration' },
  { value: 'Brand Building', apiValue: 'brand_awareness', translationKey: 'brandBuilding' }
];

const TARGET_AUDIENCE_DEFINITIONS = [
  {
    id: 'young_professionals',
    data: {
      description: 'Young professionals aged 25-35',
      demographics: 'Urban, tech-savvy, disposable income $50k+',
      interests: ['technology', 'career', 'innovation']
    }
  },
  {
    id: 'entrepreneurs',
    data: {
      description: 'Entrepreneurs and small business owners',
      demographics: 'Business owners, decision makers, 30-50 years old',
      interests: ['business', 'entrepreneurship', 'growth']
    }
  },
  {
    id: 'students',
    data: {
      description: 'Students and young adults aged 18-24',
      demographics: 'College students, entry-level professionals',
      interests: ['education', 'social media', 'trends']
    }
  },
  {
    id: 'families',
    data: {
      description: 'Parents and families with children',
      demographics: 'Parents aged 30-45, middle to upper income',
      interests: ['family', 'parenting', 'lifestyle']
    }
  },
  {
    id: 'seniors',
    data: {
      description: 'Senior adults aged 55 and above',
      demographics: 'Retirees, established professionals, 55+ years old',
      interests: ['health', 'travel', 'hobbies']
    }
  },
  {
    id: 'tech_enthusiasts',
    data: {
      description: 'Technology enthusiasts and early adopters',
      demographics: 'Tech-savvy individuals, 20-40 years old',
      interests: ['technology', 'gadgets', 'AI', 'innovation']
    }
  }
];


const ChevronDownIcon = ({ className = '' }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconBase = ({ children, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    {children}
  </svg>
);

const AlertCircleIcon = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </IconBase>
);

const ImageIcon = (props) => (
  <IconBase {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="M21 15l-5-5L5 21" />
  </IconBase>
);

const UploadIcon = (props) => (
  <IconBase {...props}>
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    <polyline points="7 9 12 4 17 9" />
    <line x1="12" y1="4" x2="12" y2="16" />
  </IconBase>
);

const XIcon = (props) => (
  <IconBase {...props}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </IconBase>
);

const FileArchiveIcon = (props) => (
  <IconBase {...props}>
    <path d="M7 3h10v18H7z" />
    <path d="M7 7h10" />
    <path d="M10 7v-4" />
  </IconBase>
);

const FileIcon = (props) => (
  <IconBase {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </IconBase>
);

const FileSpreadsheetIcon = (props) => (
  <IconBase {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M8 13h8" />
    <path d="M8 17h8" />
    <path d="M10 9h-2v10" />
    <path d="M16 9h-2v10" />
  </IconBase>
);

const FileTextIcon = (props) => (
  <IconBase {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </IconBase>
);

const HeadphonesIcon = (props) => (
  <IconBase {...props}>
    <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
    <rect x="2" y="15" width="4" height="7" rx="1" />
    <rect x="18" y="15" width="4" height="7" rx="1" />
  </IconBase>
);

const VideoIcon = (props) => (
  <IconBase {...props}>
    <rect x="3" y="5" width="15" height="14" rx="2" ry="2" />
    <polygon points="17 12 23 16 23 8 17 12" />
  </IconBase>
);

const LabeledField = ({ label, hint, error, children, showProcessing = false }) => (
  <label className={`loopino-dna-field ${error ? 'has-error' : ''}`}>
    <div className="loopino-dna-field-meta">
      <span>{label}</span>
      {hint && <small>{hint}</small>}
    </div>
    <div className="loopino-new-strategy-field-body">
      {children}
      {showProcessing && (
        <div className="loopino-new-strategy-field-overlay" aria-hidden="true">
          <Skeleton
            variant="rounded"
            width="100%"
            height="100%"
            shimmer
            className="loopino-new-strategy-field-skeleton"
          />
        </div>
      )}
    </div>
    {error && <p className="loopino-new-strategy-error">{error}</p>}
  </label>
);

const AutoResizeField = React.forwardRef(({ multiline = false, onChange, style, ...props }, ref) => {
  const innerRef = useRef(null);

  const setRefs = useCallback((node) => {
    innerRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    resize();
  }, [resize, props.value]);

  const handleChange = (event) => {
    resize();
    if (onChange) onChange(event);
  };

  const Tag = multiline ? 'textarea' : 'input';

  return (
    <Tag
      {...props}
      ref={setRefs}
      onChange={handleChange}
      onInput={resize}
      style={{ ...style, overflow: 'hidden' }}
    />
  );
});

const DropdownSelect = ({ value, options = [], placeholder, onChange }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyles, setMenuStyles] = useState({ top: 0, left: 0, width: undefined });

  const selectedLabel = useMemo(() => {
    const match = options.find((item) => (item.value ?? item.id) === value);
    return match?.label || match?.value || match?.id || '';
  }, [options, value]);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const width = rect.width;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(rect.left, 8), maxLeft);
    setMenuStyles({
      top: rect.bottom + gap,
      left,
      width,
      minWidth: width
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const handleClickOutside = (event) => {
      const triggerEl = triggerRef.current;
      const menuEl = menuRef.current;
      if (
        (triggerEl && triggerEl.contains(event.target)) ||
        (menuEl && menuEl.contains(event.target))
      ) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const handleWindowChange = () => updateMenuPosition();

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <div className="loopino-dna-dropdown" ref={triggerRef}>
      <button
        type="button"
        className="loopino-dna-dropdown-trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span>{selectedLabel || placeholder}</span>
        <ChevronDownIcon className={`loopino-new-strategy-chevron ${open ? 'is-open' : ''}`} />
      </button>
      {open && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className="loopino-dna-dropdown-menu"
          ref={menuRef}
          style={{
            position: 'fixed',
            zIndex: 1000,
            ...menuStyles
          }}
        >
          {options.map((option) => {
            const optionValue = option.value ?? option.id ?? option.label;
            const isSelected = optionValue === value;
            return (
              <button
                key={optionValue}
                type="button"
                className="loopino-dna-dropdown-item"
                data-selected={isSelected || undefined}
                onClick={() => {
                  onChange(optionValue);
                  setOpen(false);
                }}
              >
                {option.label || option.value || option.id}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / (1024 ** i);
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
};

const getFileIcon = (file) => {
  const type = file.type || file.mimeType || '';
  const name = file.name || '';

  const iconMap = [
    { match: (t, n) => t.includes('zip') || t.includes('archive') || n.endsWith('.zip') || n.endsWith('.rar'), Icon: FileArchiveIcon },
    { match: (t) => t.includes('audio/'), Icon: HeadphonesIcon },
    { match: (t, n) => t.includes('excel') || n.endsWith('.xls') || n.endsWith('.xlsx'), Icon: FileSpreadsheetIcon },
    { match: (t) => t.startsWith('image/'), Icon: ImageIcon },
    { match: (t, n) => t.includes('pdf') || n.endsWith('.pdf') || t.includes('word') || n.endsWith('.doc') || n.endsWith('.docx'), Icon: FileTextIcon },
    { match: (t) => t.includes('video/'), Icon: VideoIcon }
  ];

  for (const entry of iconMap) {
    if (entry.match(type, name)) {
      const RenderIcon = entry.Icon;
      return <RenderIcon className="coss-icon" />;
    }
  }

  return <FileIcon className="coss-icon" />;
};

const buildImageEntry = (item, fallbackName) => ({
  id: item.id || item.url || item.name || `img-${Math.random()}`,
  name: item.name || item.description || fallbackName,
  size: item.size || 0,
  preview: item.preview || item.url,
  type: item.type || 'image/*'
});

const buildDocEntry = (item, fallbackName) => ({
  id: item.id || item.url || item.name || `doc-${Math.random()}`,
  name: item.name || item.description || fallbackName,
  size: item.size || 0,
  type: item.type || 'application/octet-stream',
  url: item.preview || item.url
});

const useUploadList = (sourceItems, mapper) => {
  const [list, setList] = useState(() => (sourceItems || []).map(mapper));

  useEffect(() => {
    setList((sourceItems || []).map(mapper));
  }, [mapper, sourceItems]);

  return [list, setList];
};

const CossImageUploader = ({
  accept = 'image/svg+xml,image/png,image/jpeg,image/jpg,image/gif',
  maxFiles = 6,
  maxSizeMB = 5,
  files = [],
  onAddFiles,
  onRemoveFile,
  single = false,
  label
}) => {
  const { t } = useLanguage();
  const maxSize = maxSizeMB * 1024 * 1024;
  const imageFallbackName = t('desktop.upload.defaults.imageName', { defaultValue: 'Immagine' });
  const imageEntryMapper = useCallback((item) => buildImageEntry(item, imageFallbackName), [imageFallbackName]);
  const [localFiles, setLocalFiles] = useUploadList(files, imageEntryMapper);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);

  const openFileDialog = () => inputRef.current?.click();

  const normalizeSelection = (fileList) => {
    const incoming = Array.from(fileList || []);
    const nextErrors = [];
    const acceptList = accept.split(',').map((item) => item.trim());

    const valid = incoming.filter((file) => {
      const isAllowed = acceptList.some((pattern) => {
        if (!pattern) return false;
        if (pattern.endsWith('/*')) {
          const base = pattern.replace('/*', '');
          return file.type.startsWith(`${base}/`);
        }
        return file.type === pattern;
      });

      if (!isAllowed) {
        nextErrors.push(
          t('desktop.upload.errors.unsupportedFormat', {
            name: file.name,
            defaultValue: '{{name}}: formato non supportato'
          })
        );
        return false;
      }
      if (file.size > maxSize) {
        nextErrors.push(
          t('desktop.upload.errors.tooLarge', {
            name: file.name,
            maxSizeMB,
            defaultValue: '{{name}}: supera {{maxSizeMB}}MB'
          })
        );
        return false;
      }
      return true;
    });

    setErrors(nextErrors);
    return valid.slice(0, single ? 1 : Math.max(0, maxFiles - localFiles.length));
  };

  const addFiles = async (fileList) => {
    const valid = normalizeSelection(fileList);
    if (!valid.length) return;

    const previewEntries = valid.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      type: file.type
    }));

    if (single && localFiles.length > 0 && onRemoveFile) {
      onRemoveFile(localFiles[0].id);
    }

    setLocalFiles((prev) => {
      if (single) return previewEntries;
      return [...prev, ...previewEntries].slice(0, maxFiles);
    });

    await onAddFiles(valid);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    await addFiles(event.dataTransfer.files);
  };

  const handleChange = async (event) => {
    await addFiles(event.target.files);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemove = (id) => {
    onRemoveFile(id);
    setLocalFiles((prev) => prev.filter((file) => file.id !== id));
  };

  return (
    <div className="coss-upload-block">
      <div
        className="coss-dropzone"
        data-dragging={isDragging || undefined}
        data-files={localFiles.length > 0 || undefined}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={!single}
          aria-label={t('desktop.upload.imageInputAria', { defaultValue: 'Carica immagine' })}
          className="coss-hidden-input"
          onChange={handleChange}
        />

        {localFiles.length > 0 ? (
          <div className="coss-files-stack">
            <div className="coss-files-head">
              <h3 className="coss-files-title">
                {(label || t('desktop.upload.uploadedFiles', { defaultValue: 'File caricati' }))} ({localFiles.length})
              </h3>
              <button
                type="button"
                className="loopino-button"
                data-variant="surface"
                onClick={openFileDialog}
                disabled={localFiles.length >= maxFiles}
              >
                <UploadIcon className="coss-icon" />
                {t('desktop.upload.add', { defaultValue: 'Aggiungi' })}
              </button>
            </div>

            <div className="coss-thumb-grid">
              {localFiles.map((file) => (
                <div className="coss-thumb" key={file.id}>
                  {file.preview ? (
                    <img
                      alt={file.name}
                      className="coss-thumb-img"
                      src={file.preview}
                    />
                  ) : (
                    <div className="coss-thumb-fallback">
                      <ImageIcon className="coss-icon" />
                    </div>
                  )}
                  <button
                    aria-label={t('desktop.upload.removeImageAria', { defaultValue: 'Rimuovi immagine' })}
                    className="coss-thumb-remove"
                    type="button"
                    onClick={() => handleRemove(file.id)}
                  >
                    <XIcon className="coss-icon" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="coss-empty">
            <div className="coss-empty-icon">
              <FileTextIcon className="coss-icon" />
            </div>
            <p className="coss-empty-title">{t('desktop.upload.imagesDropTitle', { defaultValue: 'Trascina qui le immagini' })}</p>
            <p className="coss-empty-sub">
              {t('desktop.upload.imagesDropHint', {
                maxSizeMB,
                defaultValue: 'SVG, PNG, JPG o GIF (max {{maxSizeMB}}MB)'
              })}
            </p>
            <button
              type="button"
              className="loopino-button"
              data-variant="surface"
              onClick={openFileDialog}
            >
              <UploadIcon className="coss-icon" />
              {t('desktop.upload.imagesSelect', { defaultValue: 'Seleziona immagini' })}
            </button>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="coss-error" role="alert">
          <AlertCircleIcon className="coss-icon" />
          <span>{errors[0]}</span>
        </div>
      )}

    </div>
  );
};

const simulateUpload = (totalBytes, onProgress, onComplete) => {
  let timeoutId;
  let uploadedBytes = 0;
  let lastProgressReport = 0;

  const simulateChunk = () => {
    const chunkSize = Math.floor(Math.random() * 300000) + 2000;
    uploadedBytes = Math.min(totalBytes, uploadedBytes + chunkSize);
    const progressPercent = Math.floor((uploadedBytes / totalBytes) * 100);

    if (progressPercent > lastProgressReport) {
      lastProgressReport = progressPercent;
      onProgress(progressPercent);
    }

    if (uploadedBytes < totalBytes) {
      const delay = Math.floor(Math.random() * 450) + 50;
      const extraDelay = Math.random() < 0.05 ? 500 : 0;
      timeoutId = setTimeout(simulateChunk, delay + extraDelay);
    } else {
      onComplete();
    }
  };

  timeoutId = setTimeout(simulateChunk, 100);

  return () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
};

const CossFileUploader = ({
  maxFiles = 6,
  maxSizeMB = 5,
  files = [],
  onAddFiles,
  onRemoveFile
}) => {
  const { t } = useLanguage();
  const maxSize = maxSizeMB * 1024 * 1024;
  const documentFallbackName = t('desktop.upload.defaults.documentName', { defaultValue: 'Documento' });
  const docEntryMapper = useCallback((item) => buildDocEntry(item, documentFallbackName), [documentFallbackName]);
  const [localFiles, setLocalFiles] = useUploadList(files, docEntryMapper);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const inputRef = useRef(null);

  const handleFileRemoved = (fileId) => {
    setUploadProgress((prev) => prev.filter((item) => item.fileId !== fileId));
  };

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const nextErrors = [];
    const valid = incoming.filter((file) => {
      if (file.size > maxSize) {
        nextErrors.push(
          t('desktop.upload.errors.tooLarge', {
            name: file.name,
            maxSizeMB,
            defaultValue: '{{name}}: supera {{maxSizeMB}}MB'
          })
        );
        return false;
      }
      return true;
    }).slice(0, Math.max(0, maxFiles - localFiles.length));

    setErrors(nextErrors);
    if (!valid.length) return;

    const previewEntries = valid.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setLocalFiles((prev) => [...prev, ...previewEntries].slice(0, maxFiles));

    const newProgressItems = previewEntries.map((file) => ({
      completed: false,
      fileId: file.id,
      progress: 0
    }));
    setUploadProgress((prev) => [...prev, ...newProgressItems]);

    const cleanupFns = [];
    newProgressItems.forEach((progressItem, index) => {
      const file = valid[index];
      const cleanup = simulateUpload(
        file.size,
        (progress) => {
          setUploadProgress((prev) =>
            prev.map((item) => (item.fileId === progressItem.fileId ? { ...item, progress } : item))
          );
        },
        () => {
          setUploadProgress((prev) =>
            prev.map((item) => (item.fileId === progressItem.fileId ? { ...item, completed: true } : item))
          );
        }
      );
      cleanupFns.push(cleanup);
    });

    await onAddFiles(valid);

    return () => {
      cleanupFns.forEach((cleanup) => cleanup && cleanup());
    };
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    await addFiles(event.dataTransfer.files);
  };

  const handleChange = async (event) => {
    await addFiles(event.target.files);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="coss-upload-block">
      <div
        className="coss-dropzone"
        data-dragging={isDragging || undefined}
        data-files={localFiles.length > 0 || undefined}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          aria-label={t('desktop.upload.fileInputAria', { defaultValue: 'Carica file' })}
          className="coss-hidden-input"
          onChange={handleChange}
        />

        {localFiles.length > 0 ? (
            <div className="coss-files-stack">
              <div className="coss-files-head">
                <h3 className="coss-files-title">
                  {t('desktop.upload.uploadedFiles', { defaultValue: 'File caricati' })} ({localFiles.length})
                </h3>
              <div className="coss-actions">
                <button
                  type="button"
                  className="loopino-button"
                  data-variant="surface"
                  onClick={() => inputRef.current?.click()}
                >
                  <UploadIcon className="coss-icon" />
                  {t('desktop.upload.addFiles', { defaultValue: 'Aggiungi file' })}
                </button>
              </div>
              </div>

            <div className="coss-file-list">
              {localFiles.map((file) => {
                const progressInfo = uploadProgress.find((p) => p.fileId === file.id);
                const isUploading = progressInfo && !progressInfo.completed;

                return (
                  <div
                    className="coss-file-card"
                    data-uploading={isUploading || undefined}
                    key={file.id}
                  >
                    <div className="coss-file-row">
                      <div className="coss-file-meta">
                        <div className="coss-file-icon">
                          {getFileIcon(file)}
                        </div>
                        <div className="coss-file-info">
                          <p className="coss-file-name">{file.name}</p>
                          <p className="coss-file-size">{formatBytes(file.size)}</p>
                        </div>
                      </div>
                      <button
                        aria-label={t('desktop.upload.removeFileAria', { defaultValue: 'Rimuovi file' })}
                        className="coss-remove-btn"
                        type="button"
                        onClick={() => {
                          handleFileRemoved(file.id);
                          onRemoveFile(file.id);
                        }}
                      >
                        <XIcon className="coss-icon" />
                      </button>
                    </div>

                    {progressInfo && !progressInfo.completed && (
                      <div className="coss-progress">
                        <div className="coss-progress-bar">
                          <div
                            className="coss-progress-fill"
                            style={{ width: `${progressInfo.progress || 0}%` }}
                          />
                        </div>
                        <span className="coss-progress-value">
                          {progressInfo.progress || 0}%
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="coss-empty">
            <div className="coss-empty-icon">
              <ImageIcon className="coss-icon" />
            </div>
            <p className="coss-empty-title">{t('desktop.upload.filesDropTitle', { defaultValue: 'Trascina qui i file' })}</p>
            <p className="coss-empty-sub">
              {t('desktop.upload.filesDropHint', {
                maxFiles,
                maxSizeMB,
                defaultValue: 'Max {{maxFiles}} file · Fino a {{maxSizeMB}}MB'
              })}
            </p>
            <button
              type="button"
              className="loopino-button"
              data-variant="surface"
              onClick={() => inputRef.current?.click()}
            >
              <UploadIcon className="coss-icon" />
              {t('desktop.upload.filesSelect', { defaultValue: 'Seleziona file' })}
            </button>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="coss-error" role="alert">
          <AlertCircleIcon className="coss-icon" />
          <span>{errors[0]}</span>
        </div>
      )}

    </div>
  );
};

const DesktopNewStrategyContent = ({ onNavigate }) => {
  const {
    createCampaign,
    loading: campaignLoading,
    error: campaignError,
    clearError: clearCampaignError
  } = useCampaign();
  const { currentCompany, loading: companyLoading } = useCompany();
  const { t, language } = useLanguage();
  const { notify } = useDesktopAlert();
  const dnaGateRef = useRef(false);

  const [campaignName, setCampaignName] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState(3);
  const [startDate, setStartDate] = useState('');
  const [socialChannels, setSocialChannels] = useState({ instagram: false });
  const [selectedTone, setSelectedTone] = useState('');
  const [selectedObjective, setSelectedObjective] = useState('');
  const [selectedTargetAudience, setSelectedTargetAudience] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState({ pdf: [], image: [] });
  const [touchedFields, setTouchedFields] = useState({
    campaignName: false,
    description: false,
    selectedObjective: false,
    startDate: false,
    socialChannels: false,
    selectedTone: false,
    selectedTargetAudience: false
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);

  const previewUrlsRef = useRef(new Set());
  const datePickerRef = useRef(null);
  const dateTriggerRef = useRef(null);

  const isLoading = campaignLoading && !isSubmitting;
  const isProcessing = isSubmitting;

  const buildStrategizerDnaPath = useCallback((dnaGate) => {
    const params = new URLSearchParams();
    params.set('view', 'dna');
    const companyId = currentCompany?.id ||
      currentCompany?._id ||
      currentCompany?.company_id ||
      currentCompany?.company?.id ||
      currentCompany?.company?._id ||
      currentCompany?.company?.company_id;
    if (companyId) {
      params.set('companyId', companyId);
    }
    if (dnaGate) {
      params.set('dnaGate', dnaGate);
    }
    return `/strategizer?${params.toString()}`;
  }, [currentCompany]);

  useEffect(() => {
    if (companyLoading) return;
    if (!currentCompany) return;
    if (hasCompanyDna(currentCompany)) return;
    if (dnaGateRef.current) return;
    dnaGateRef.current = true;

    notify({
      variant: 'warning',
      title: t('newCampaign.alerts.dnaRequired', {
        defaultValue: 'Prima di creare una strategia crea il tuo DNA.'
      })
    });

    if (typeof onNavigate === 'function') {
      onNavigate(buildStrategizerDnaPath('strategy'));
    } else if (typeof window !== 'undefined') {
      window.history.pushState({}, '', buildStrategizerDnaPath('strategy'));
      window.dispatchEvent(new Event('popstate'));
    }
  }, [buildStrategizerDnaPath, companyLoading, currentCompany, notify, onNavigate, t]);

  const resolvedLocale = useMemo(() => {
    if (!language) {
      return 'it-IT';
    }

    if (language.startsWith('it')) {
      return 'it-IT';
    }

    if (language.startsWith('en')) {
      return 'en-US';
    }

    return language;
  }, [language]);

  const toneOptions = useMemo(() => {
    return TONE_OPTIONS.map((option) => ({
      ...option,
      label: t(`newCampaign.fields.tone.options.${option.translationKey}`)
    }));
  }, [t, language]);

  const objectiveOptions = useMemo(() => {
    return OBJECTIVE_OPTIONS.map((option) => ({
      ...option,
      label: t(`newCampaign.fields.objective.options.${option.translationKey}`)
    }));
  }, [t, language]);

  const targetAudienceOptions = useMemo(() => {
    return TARGET_AUDIENCE_DEFINITIONS.map((option) => ({
      ...option,
      label: t(`newCampaign.fields.targetAudience.options.${option.id}`)
    }));
  }, [t, language]);

  const frequencyOptions = useMemo(() => {
    return [1, 2, 3, 4, 5, 6, 7].map((value) => ({
      value,
      label: t('newCampaign.fields.frequency.option', { count: value })
    }));
  }, [t, language]);

  const weekdayLabels = useMemo(() => {
    const labels = t('newCampaign.calendar.weekdaysShort', { returnObjects: true });
    if (Array.isArray(labels) && labels.length === 7) {
      return labels;
    }

    return language?.startsWith('it')
      ? ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  }, [t, language]);

  const monthFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { month: 'long', year: 'numeric' });
    } catch (error) {
      return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
    }
  }, [resolvedLocale]);

  const defaultProcessingMessages = useMemo(() => (
    language?.startsWith('it')
      ? [
        'Accendo i motori creativi...',
        'Mescolo insight e un pizzico di glitter digitale.',
        'Un attimo, sto chiedendo all\'AI di fare la magia.',
        'Allineo pixel e idee come fossero LEGO.'
      ]
      : [
        'Warming up the creativity engines...',
        'Mixing insights with a pinch of sparkle.',
        'One second, asking the AI to work its magic.',
        'Aligning pixels and ideas like LEGO bricks.'
      ]
  ), [language]);

  const processingMessages = useMemo(() => {
    const messages = t('newCampaign.processing.messages', {
      returnObjects: true,
      defaultValue: defaultProcessingMessages
    });
    return Array.isArray(messages) && messages.length ? messages : defaultProcessingMessages;
  }, [t, defaultProcessingMessages]);

  const startDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }, [resolvedLocale]);

  const displayStartDate = useMemo(() => {
    if (!startDate) {
      return '';
    }
    const parsedDate = new Date(startDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }
    return startDateFormatter.format(parsedDate);
  }, [startDate, startDateFormatter]);

  const isFormValid = useMemo(() => {
    if (!campaignName.trim()) return false;
    if (!description.trim()) return false;
    if (!selectedObjective) return false;
    if (!selectedTone) return false;
    if (!selectedTargetAudience) return false;
    if (!startDate) return false;
    const hasSelectedChannel = Object.values(socialChannels).some((channel) => channel);
    if (!hasSelectedChannel) return false;
    return true;
  }, [campaignName, description, selectedObjective, selectedTone, selectedTargetAudience, startDate, socialChannels]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!isProcessing) {
      setProcessingMessageIndex(0);
      return;
    }
    if (processingMessages.length < 2) return;
    const interval = setInterval(() => {
      setProcessingMessageIndex((prev) => (prev + 1) % processingMessages.length);
    }, 2600);
    return () => clearInterval(interval);
  }, [isProcessing, processingMessages]);

  const markFieldAsTouched = (fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const handleNavigateBack = useCallback(() => {
    if (onNavigate) {
      onNavigate('/desktop/strategy');
    }
  }, [onNavigate]);

  const toggleSocialChannel = (channel) => {
    setSocialChannels((prev) => ({
      ...prev,
      [channel]: !prev[channel]
    }));
    markFieldAsTouched('socialChannels');
  };

  const revokePreview = useCallback((file) => {
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
      previewUrlsRef.current.delete(file.preview);
    }
  }, []);

  const handleSupportUploadAdd = useCallback((fileType) => async (files) => {
    const entries = (files || []).map((file) => {
      const preview = fileType === 'image' ? URL.createObjectURL(file) : undefined;
      if (preview) {
        previewUrlsRef.current.add(preview);
      }
      return {
        id: `${fileType}-${file.name}-${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        preview,
        file
      };
    });

    if (!entries.length) return;

    setUploadedFiles((prev) => ({
      ...prev,
      [fileType]: [...prev[fileType], ...entries]
    }));
  }, []);

  const handleSupportUploadRemove = useCallback((fileType) => (fileId) => {
    setUploadedFiles((prev) => {
      const toRemove = prev[fileType].find((item) => item.id === fileId);
      if (toRemove) {
        revokePreview(toRemove);
      }
      return {
        ...prev,
        [fileType]: prev[fileType].filter((item) => item.id !== fileId)
      };
    });
  }, [revokePreview]);

  const resetForm = () => {
    setCampaignName('');
    setDescription('');
    setFrequency(3);
    setStartDate('');
    setSocialChannels({ instagram: false });
    setSelectedTone('');
    setSelectedObjective('');
    setSelectedTargetAudience('');
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
    setUploadedFiles({ pdf: [], image: [] });
    setTouchedFields({
      campaignName: false,
      description: false,
      selectedObjective: false,
      startDate: false,
      socialChannels: false,
      selectedTone: false,
      selectedTargetAudience: false
    });
    setShowDatePicker(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < (startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1); i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const navigatePickerMonth = (direction) => {
    const newMonth = new Date(pickerMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setPickerMonth(newMonth);
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    setStartDate(formattedDate);
    setShowDatePicker(false);
    markFieldAsTouched('startDate');
  };

  const closeDatePicker = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setShowDatePicker(false);
  }, []);

  useEffect(() => {
    if (!showDatePicker || typeof document === 'undefined') return undefined;
    const handleClickOutside = (event) => {
      if (datePickerRef.current?.contains(event.target)) return;
      if (dateTriggerRef.current?.contains(event.target)) return;
      setShowDatePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleCreateCampaign = async () => {
    clearCampaignError();
    if (!currentCompany) {
      notify({
        variant: 'warning',
        title: t('newCampaign.alerts.noCompany')
      });
      return;
    }
    if (!hasCompanyDna(currentCompany)) {
      notify({
        variant: 'warning',
        title: t('newCampaign.alerts.dnaRequired', {
          defaultValue: 'Prima di creare una strategia crea il tuo DNA.'
        })
      });
      if (typeof onNavigate === 'function') {
        onNavigate(buildStrategizerDnaPath('strategy'));
      } else if (typeof window !== 'undefined') {
        window.history.pushState({}, '', buildStrategizerDnaPath('strategy'));
        window.dispatchEvent(new Event('popstate'));
      }
      return;
    }
    setIsSubmitting(true);
    try {
      const hasFiles = uploadedFiles.pdf.length > 0 || uploadedFiles.image.length > 0;
      const targetAudienceEntry = selectedTargetAudience
        ? targetAudienceOptions.find((opt) => opt.id === selectedTargetAudience)
        : null;
      const targetAudienceData = targetAudienceEntry?.data || {
        description: 'General audience',
        demographics: 'All demographics',
        interests: ['general']
      };
      const objectiveApiValue = OBJECTIVE_OPTIONS.find((option) => option.value === selectedObjective)?.apiValue || 'brand_awareness';
      const campaignTone = selectedTone || 'Professional';
      const campaignNameValue = campaignName || t('newCampaign.defaults.campaignName');

      const campaignPayload = {
        company_id: currentCompany.id,
        name: campaignNameValue,
        description: description || '',
        objective: objectiveApiValue,
        target_platforms: Object.keys(socialChannels).filter((channel) => socialChannels[channel]),
        posting_frequency: frequency,
        communication_tone: campaignTone,
        start_date: startDate ? `${startDate}T00:00:00.000Z` : new Date().toISOString(),
        target_audience: targetAudienceData,
        budget: 1000.0
      };

      let newCampaign = null;
      if (hasFiles) {
        const formData = new FormData();
        formData.append('campaign_data', JSON.stringify(campaignPayload));
        uploadedFiles.pdf.forEach((fileEntry) => {
          const fileToSend = fileEntry.file || fileEntry;
          formData.append('pdf_files', fileToSend);
        });
        uploadedFiles.image.forEach((fileEntry) => {
          const fileToSend = fileEntry.file || fileEntry;
          formData.append('image_files', fileToSend);
        });
        newCampaign = await createCampaign(formData, { isFormData: true });
      } else {
        newCampaign = await createCampaign(campaignPayload);
      }

      notify({
        variant: 'success',
        title: t('newCampaign.alerts.created', { defaultValue: 'Strategia creata' })
      });
      resetForm();

      if (onNavigate) {
        onNavigate('/desktop/strategy', { state: { targetCampaignId: newCampaign?.id || newCampaign?._id } });
      }
    } catch (error) {
      const fallbackMessage = error?.message || t('newCampaign.alerts.unknownError');
      notify({
        variant: 'error',
        title: t('newCampaign.alerts.creationFailed', {
          error: fallbackMessage,
          defaultValue: `Creazione fallita: ${fallbackMessage}`
        })
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSectionSkeleton = (fieldHeights = []) => (
    <section className="loopino-card loopino-new-strategy-card">
      <div className="loopino-card-header">
        <Skeleton variant="rounded" width={160} height={14} shimmer />
        <Skeleton variant="rounded" width={240} height={26} shimmer />
        <Skeleton variant="rounded" width="80%" height={16} shimmer />
      </div>
      <div className="loopino-dna-fields-stack">
        {fieldHeights.map((height, index) => (
          <Skeleton
            key={`loopino-new-strategy-skeleton-${index}`}
            variant="rounded"
            width="100%"
            height={height}
            shimmer
          />
        ))}
      </div>
    </section>
  );

  const renderUploadSkeleton = () => (
    <section className="loopino-card loopino-new-strategy-card">
      <div className="loopino-card-header">
        <Skeleton variant="rounded" width={160} height={14} shimmer />
        <Skeleton variant="rounded" width={280} height={26} shimmer />
        <Skeleton variant="rounded" width="82%" height={16} shimmer />
      </div>
      <div className="loopino-new-strategy-upload-grid">
        <Skeleton variant="rounded" width="100%" height={180} shimmer />
        <Skeleton variant="rounded" width="100%" height={180} shimmer />
      </div>
    </section>
  );

  const isBootstrappingCompany = companyLoading && !currentCompany;
  const isDnaMissing = Boolean(currentCompany) && !hasCompanyDna(currentCompany);
  const shouldHoldForGate = isBootstrappingCompany || isDnaMissing;

  if (shouldHoldForGate) {
    return (
      <div className="loopino-desktop-shell loopino-new-strategy-shell">
        <header className="loopino-desktop-post-review-header loopino-new-strategy-header">
          <div className="loopino-new-strategy-hero">
            <Skeleton variant="rounded" width={180} height={14} shimmer />
            <Skeleton variant="rounded" width={320} height={34} shimmer />
            <Skeleton variant="rounded" width={520} height={18} shimmer />
          </div>
          <div className="loopino-new-strategy-actions">
            <Skeleton variant="rounded" width={220} height={44} shimmer />
          </div>
        </header>
        <div className="loopino-desktop-grid loopino-new-strategy-grid">
          {renderSectionSkeleton([64, 120])}
          {renderSectionSkeleton([64, 64, 64])}
        </div>
        <div className="loopino-desktop-grid loopino-new-strategy-grid">
          {renderSectionSkeleton([68, 68, 120])}
          {renderUploadSkeleton()}
        </div>
        <div className="loopino-new-strategy-footer">
          <div className="loopino-new-strategy-actions">
            <Skeleton variant="rounded" width={240} height={56} shimmer />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loopino-desktop-shell loopino-new-strategy-shell">
      <div
        className={`loopino-new-strategy-content ${isProcessing ? 'is-processing' : ''}`}
        aria-busy={isProcessing}
        inert={isProcessing ? '' : undefined}
      >
        <header className="loopino-desktop-post-review-header loopino-new-strategy-header">
          <div className="loopino-new-strategy-hero">
            {isLoading ? (
              <>
                <Skeleton variant="rounded" width={180} height={14} shimmer />
                <Skeleton variant="rounded" width={320} height={34} shimmer />
                <Skeleton variant="rounded" width={520} height={18} shimmer />
              </>
            ) : (
              <>
                <p className="loopino-desktop-post-review-eyebrow">
                  {t('newCampaign.desktop.eyebrow', { defaultValue: 'Loopino desktop' })}
                </p>
                <h1>{t('newCampaign.desktop.title', { defaultValue: 'Nuova strategia' })}</h1>
                <p className="loopino-desktop-post-review-summary">
                  {t('newCampaign.desktop.subtitle', {
                    defaultValue: 'Crea una nuova campagna con gli stessi campi della vista mobile, usando il layout desktop.'
                  })}
                </p>
              </>
            )}
          </div>
          <div className="loopino-new-strategy-actions">
            {isLoading ? (
              <Skeleton variant="rounded" width={220} height={44} shimmer />
            ) : (
              <button
                type="button"
                className="loopino-button"
                data-variant="surface"
                onClick={handleNavigateBack}
                disabled={isProcessing || isLoading}
              >
                {t('newCampaign.desktop.back', { defaultValue: 'Torna alla strategia' })}
              </button>
            )}
          </div>
        </header>

        {campaignError && (
          <div className="loopino-new-strategy-alert" role="alert">
            <div>
              <p className="loopino-new-strategy-alert-title">
                {t('newCampaign.alerts.creationFailed', { error: '', defaultValue: 'Errore durante la creazione' })}
              </p>
              <p className="loopino-new-strategy-alert-copy">{campaignError}</p>
            </div>
            <button type="button" className="loopino-button" data-variant="ghost" onClick={clearCampaignError}>
              {t('newCampaign.alerts.dismiss', { defaultValue: 'Chiudi' })}
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="loopino-desktop-grid loopino-new-strategy-grid">
            {renderSectionSkeleton([64, 120])}
            {renderSectionSkeleton([64, 64, 64])}
          </div>
        ) : (
          <div className="loopino-desktop-grid loopino-new-strategy-grid">
            <section className="loopino-card loopino-new-strategy-card">
              <div className="loopino-card-header">
                <div className="loopino-stack" data-gap="xs">
                  <p className="loopino-eyebrow">{t('newCampaign.sections.brief', { defaultValue: 'Brief strategico' })}</p>
                  <h2 className="loopino-card-title">
                    {t('newCampaign.fields.name.label', { defaultValue: 'Informazioni campagna' })}
                  </h2>
                  <p className="loopino-card-description">
                    {t('newCampaign.desktop.briefDescription', {
                      defaultValue: 'Definisci nome, descrizione e obiettivi così come nella versione mobile.'
                    })}
                  </p>
                </div>
              </div>

              <div className="loopino-dna-fields-stack">
                <LabeledField
                  label={t('newCampaign.fields.name.label', { defaultValue: 'Nome campagna' })}
                  error={touchedFields.campaignName && !campaignName.trim() ? t('newCampaign.fields.name.error') : ''}
                  showProcessing={isProcessing}
                >
                  <div className={`loopino-dna-input-with-icon ${touchedFields.campaignName && !campaignName.trim() ? 'has-error' : ''}`}>
                    <img src={iconTarget} alt="" />
                    <AutoResizeField
                      className="loopino-dna-input"
                      type="text"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      onBlur={() => markFieldAsTouched('campaignName')}
                      placeholder={t('newCampaign.fields.name.placeholder')}
                    />
                  </div>
                </LabeledField>

                <LabeledField
                  label={t('newCampaign.fields.description.label', { defaultValue: 'Descrizione' })}
                  error={touchedFields.description && !description.trim() ? t('newCampaign.fields.description.error') : ''}
                  showProcessing={isProcessing}
                >
                  <div className={`loopino-dna-input-with-icon ${touchedFields.description && !description.trim() ? 'has-error' : ''}`}>
                    <img src={iconDescription} alt="" />
                    <AutoResizeField
                      className="loopino-dna-textarea"
                      rows={3}
                      multiline
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      onBlur={() => markFieldAsTouched('description')}
                      placeholder={t('newCampaign.fields.description.placeholder')}
                    />
                  </div>
                </LabeledField>
              </div>
            </section>

            <section className="loopino-card loopino-new-strategy-card">
              <div className="loopino-card-header">
                <div className="loopino-stack" data-gap="xs">
                  <p className="loopino-eyebrow">{t('newCampaign.sections.objectives', { defaultValue: 'Obiettivi' })}</p>
                  <h2 className="loopino-card-title">
                    {t('newCampaign.desktop.objectivesTitle', { defaultValue: 'Obiettivo, tono e target' })}
                  </h2>
                  <p className="loopino-card-description">
                    {t('newCampaign.desktop.objectivesLead', {
                      defaultValue: 'Seleziona le opzioni dai menu a tendina con lo stesso schema della vista mobile.'
                    })}
                  </p>
                </div>
              </div>

              <div className="loopino-dna-fields-stack">
                <LabeledField
                  label={t('newCampaign.fields.objective.label', { defaultValue: 'Obiettivo' })}
                  error={touchedFields.selectedObjective && !selectedObjective ? t('newCampaign.fields.objective.error') : ''}
                  showProcessing={isProcessing}
                >
                  <DropdownSelect
                    value={selectedObjective}
                    onChange={(value) => setSelectedObjective(value)}
                    options={objectiveOptions}
                    placeholder={t('newCampaign.fields.objective.placeholder')}
                  />
                </LabeledField>

                <LabeledField
                  label={t('newCampaign.fields.tone.label', { defaultValue: 'Tono di voce' })}
                  error={touchedFields.selectedTone && !selectedTone ? t('newCampaign.fields.tone.error') : ''}
                  showProcessing={isProcessing}
                >
                  <DropdownSelect
                    value={selectedTone}
                    onChange={(value) => setSelectedTone(value)}
                    options={toneOptions}
                    placeholder={t('newCampaign.fields.tone.placeholder')}
                  />
                </LabeledField>

                <LabeledField
                  label={t('newCampaign.fields.targetAudience.label', { defaultValue: 'Target' })}
                  error={touchedFields.selectedTargetAudience && !selectedTargetAudience ? t('newCampaign.fields.targetAudience.error') : ''}
                  showProcessing={isProcessing}
                >
                  <DropdownSelect
                    value={selectedTargetAudience}
                    onChange={(value) => setSelectedTargetAudience(value)}
                    options={targetAudienceOptions}
                    placeholder={t('newCampaign.fields.targetAudience.placeholder')}
                  />
                </LabeledField>
              </div>
            </section>
          </div>
        )}

      {isLoading ? (
        <div className="loopino-desktop-grid loopino-new-strategy-grid">
          {renderSectionSkeleton([68, 68, 120])}
          {renderUploadSkeleton()}
        </div>
      ) : (
        <div className="loopino-desktop-grid loopino-new-strategy-grid">
          <section className="loopino-card loopino-new-strategy-card">
            <div className="loopino-card-header">
              <div className="loopino-stack" data-gap="xs">
                <p className="loopino-eyebrow">{t('newCampaign.sections.planning', { defaultValue: 'Pianificazione' })}</p>
                <h2 className="loopino-card-title">
                  {t('newCampaign.fields.startDate.label', { defaultValue: 'Timing e canali' })}
                </h2>
                <p className="loopino-card-description">
                  {t('newCampaign.desktop.planningLead', {
                    defaultValue: 'Imposta data di inizio, frequenza settimanale e canali da attivare.'
                  })}
                </p>
              </div>
            </div>

            <div className="loopino-dna-fields-grid">
              <LabeledField
                label={t('newCampaign.fields.startDate.label')}
                error={touchedFields.startDate && !startDate ? t('newCampaign.fields.startDate.error') : ''}
                showProcessing={isProcessing}
              >
                <button
                  type="button"
                  className={`loopino-dna-dropdown-trigger loopino-new-strategy-date ${touchedFields.startDate && !startDate ? 'has-error' : ''}`}
                  onClick={() => {
                    setPickerMonth(startDate ? new Date(startDate) : new Date());
                    setShowDatePicker((prev) => !prev);
                  }}
                  ref={dateTriggerRef}
                >
                  <div className="loopino-new-strategy-date-icon">
                    <img src={iconCalendar} alt="" />
                  </div>
                  <div className="loopino-new-strategy-date-copy">
                    <span className="loopino-select-label">{t('newCampaign.fields.startDate.label')}</span>
                    <span className="loopino-select-value">
                      {displayStartDate || t('newCampaign.fields.startDate.placeholder')}
                    </span>
                  </div>
                  <ChevronDownIcon className={`loopino-new-strategy-chevron ${showDatePicker ? 'is-open' : ''}`} />
                </button>
                {showDatePicker && (
                  <div className="loopino-new-strategy-date-picker" ref={datePickerRef}>
                    <div className="loopino-new-strategy-date-head">
                      <button
                        type="button"
                        className="loopino-button"
                        data-variant="ghost"
                        onClick={() => navigatePickerMonth(-1)}
                        aria-label={t('postReview.postEdit.time.prevMonth', { defaultValue: 'Mese precedente' })}
                      >
                        ‹
                      </button>
                      <div className="loopino-new-strategy-month">{monthFormatter.format(pickerMonth)}</div>
                      <button
                        type="button"
                        className="loopino-button"
                        data-variant="ghost"
                        onClick={() => navigatePickerMonth(1)}
                        aria-label={t('postReview.postEdit.time.nextMonth', { defaultValue: 'Mese successivo' })}
                      >
                        ›
                      </button>
                    </div>
                    <div className="loopino-new-strategy-weekdays">
                      {weekdayLabels.map((day) => (
                        <span key={day}>{day}</span>
                      ))}
                    </div>
                    <div className="loopino-new-strategy-date-grid">
                      {getDaysInMonth(pickerMonth).map((date, index) => {
                        const isTodayDate = date && isToday(date);
                        const isSelected = date && startDate && new Date(startDate).toDateString() === date.toDateString();
                        return (
                          <button
                            key={index}
                            type="button"
                            className={`loopino-new-strategy-date-cell ${!date ? 'is-empty' : ''} ${isTodayDate ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => handleDateSelect(date)}
                            disabled={!date}
                          >
                            {date ? date.getDate() : ''}
                          </button>
                        );
                      })}
                    </div>
                    <div className="loopino-new-strategy-date-actions">
                      <button type="button" className="loopino-button" data-variant="ghost" onClick={closeDatePicker}>
                        {t('newCampaign.fields.startDate.close', { defaultValue: 'Chiudi' })}
                      </button>
                      <button type="button" className="loopino-button" data-variant="surface" onClick={() => handleDateSelect(new Date())}>
                        {t('newCampaign.fields.startDate.today', { defaultValue: 'Oggi' })}
                      </button>
                    </div>
                  </div>
                )}
              </LabeledField>

              <LabeledField
                label={t('newCampaign.fields.frequency.label')}
                showProcessing={isProcessing}
              >
                <div className="loopino-dna-input-with-icon">
                  <img src={iconFrequency} alt="" />
                  <DropdownSelect
                    value={frequency}
                    onChange={(value) => setFrequency(Number(value))}
                    options={frequencyOptions}
                    placeholder={t('newCampaign.fields.frequency.placeholder', { defaultValue: 'Seleziona frequenza' })}
                  />
                </div>
              </LabeledField>
            </div>

            <div className="loopino-dna-fields-stack">
              <LabeledField
                label={t('newCampaign.fields.socialChannels.label', { defaultValue: 'Canali social' })}
                error={touchedFields.socialChannels && !Object.values(socialChannels).some((channel) => channel) ? t('newCampaign.fields.socialChannels.error') : ''}
                showProcessing={isProcessing}
              >
                <div className={`loopino-new-strategy-social ${touchedFields.socialChannels && !Object.values(socialChannels).some((channel) => channel) ? 'has-error' : ''}`}>
                  <div className="loopino-new-strategy-social-icon">
                    <img src={iconSocial} alt="" />
                  </div>
                  <div className="loopino-new-strategy-social-buttons">
                    <button
                      type="button"
                      className={`loopino-new-strategy-social-btn ${socialChannels.instagram ? 'is-active' : ''}`}
                      onClick={() => toggleSocialChannel('instagram')}
                    >
                      <img src={iconInstagram} alt="" />
                      {t('newCampaign.fields.socialChannels.options.instagram')}
                    </button>
                  </div>
                </div>
              </LabeledField>
            </div>
          </section>

          <section className="loopino-card loopino-new-strategy-card">
            <div className="loopino-card-header">
              <div className="loopino-stack" data-gap="xs">
                <p className="loopino-eyebrow">{t('newCampaign.upload.title')}</p>
                <h2 className="loopino-card-title">
                  {t('newCampaign.desktop.uploadTitle', { defaultValue: 'Materiali di supporto' })}
                </h2>
                <p className="loopino-card-description">
                  {t('newCampaign.desktop.uploadLead', {
                    defaultValue: 'Carica PDF o immagini come nella modale mobile per arricchire il brief.'
                  })}
                </p>
              </div>
            </div>

            <div className="loopino-new-strategy-upload-grid">
              <div className={`loopino-dna-upload-card loopino-new-strategy-upload ${uploadedFiles.pdf.length ? 'is-active' : ''}`}>
                <div className="loopino-dna-upload-head">
                  <div className="loopino-dna-upload-title">
                    <img src={iconPdf} alt="" />
                    <span>{t('newCampaign.upload.pdf.title')}</span>
                  </div>
                </div>
                <CossFileUploader
                  files={uploadedFiles.pdf}
                  maxFiles={6}
                  maxSizeMB={5}
                  onAddFiles={handleSupportUploadAdd('pdf')}
                  onRemoveFile={handleSupportUploadRemove('pdf')}
                />
                {isProcessing && (
                  <div className="loopino-new-strategy-upload-overlay" aria-hidden="true">
                    <Skeleton
                      variant="rounded"
                      width="92%"
                      height="78%"
                      shimmer
                      className="loopino-new-strategy-upload-skeleton"
                    />
                  </div>
                )}
              </div>

              <div className={`loopino-dna-upload-card loopino-new-strategy-upload ${uploadedFiles.image.length ? 'is-active' : ''}`}>
                <div className="loopino-dna-upload-head">
                  <div className="loopino-dna-upload-title">
                    <img src={iconImage} alt="" />
                    <span>{t('newCampaign.upload.image.title')}</span>
                  </div>
                </div>
                <CossImageUploader
                  accept="image/svg+xml,image/png,image/jpeg,image/jpg,image/gif"
                  files={uploadedFiles.image}
                  maxFiles={6}
                  maxSizeMB={5}
                  onAddFiles={handleSupportUploadAdd('image')}
                  onRemoveFile={handleSupportUploadRemove('image')}
                  label={t('newCampaign.upload.image.title')}
                />
                {isProcessing && (
                  <div className="loopino-new-strategy-upload-overlay" aria-hidden="true">
                    <Skeleton
                      variant="rounded"
                      width="92%"
                      height="78%"
                      shimmer
                      className="loopino-new-strategy-upload-skeleton"
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      <div className="loopino-new-strategy-footer">
        <div className="loopino-new-strategy-actions">
          {isLoading ? (
            <Skeleton variant="rounded" width={240} height={56} shimmer />
          ) : (
            <button
              type="button"
              className="loopino-button"
              data-variant="primary"
              onClick={handleCreateCampaign}
              disabled={isProcessing || isLoading || !isFormValid}
            >
              <img src={iconAi} alt="" />
              {isProcessing ? t('newCampaign.footer.creating') : t('newCampaign.footer.create')}
            </button>
          )}
        </div>
      </div>
      </div>
      {isProcessing && (
        <div
          className="loopino-new-strategy-overlay"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="loopino-new-strategy-loader">
            <div className="loopino-new-strategy-spinner" aria-hidden="true" />
            <p className="loopino-new-strategy-loading-title">
              {t('newCampaign.processing.title', { defaultValue: 'Creazione campagna in corso' })}
            </p>
            <p className="loopino-new-strategy-loading-message">
              {processingMessages[Math.min(processingMessageIndex, processingMessages.length - 1)]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const DesktopNewStrategyPage = ({ onNavigate }) => {
  return (
    <DesktopAlertProvider>
      <DesktopNewStrategyContent onNavigate={onNavigate} />
    </DesktopAlertProvider>
  );
};

export default DesktopNewStrategyPage;
